import { createFileRoute } from "@tanstack/react-router";
import { Calendar, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useEffect } from "react";

export const Route = createFileRoute("/blog")({
  component: Blog,
});

function Blog() {
  useEffect(() => {
    document.title = "Blog — 24Boda";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center">
            <img src="/logo.png" alt="24Boda" className="h-10 w-auto" />
          </a>
          <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
            <a href="/" className="transition-colors hover:text-foreground">
              Home
            </a>
            <a href="/about" className="transition-colors hover:text-foreground">
              About
            </a>
            <a href="/blog" className="text-foreground">
              Blog
            </a>
            <a href="/faq" className="transition-colors hover:text-foreground">
              FAQ
            </a>
            <a href="/join-riders" className="transition-colors hover:text-foreground">
              Riders
            </a>
          </nav>
          <a
            href="/#download"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Download App
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="border-b border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              24Boda Blog
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Delivery tips, rider stories, and insights from Uganda's fastest-growing boda delivery
              service.
            </p>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <BlogPostCard
                title="How to Send Packages Safely in Kampala"
                excerpt="Learn the best practices for packaging and sending your items with 24Boda."
                date="March 15, 2026"
                readTime="5 min read"
              />
              <BlogPostCard
                title="Meet Our Riders: James's Story"
                excerpt="From part-time rider to full-time entrepreneur — how 24Boda changed James's life."
                date="March 10, 2026"
                readTime="4 min read"
              />
              <BlogPostCard
                title="5 Ways Boda Delivery Saves You Time"
                excerpt="Discover how using boda delivery can help you reclaim hours in your busy day."
                date="March 5, 2026"
                readTime="3 min read"
              />
              <BlogPostCard
                title="Understanding Our Pricing"
                excerpt="Transparent pricing explained: how we calculate fares and why we're fair."
                date="February 28, 2026"
                readTime="6 min read"
              />
              <BlogPostCard
                title="Safety First: Our Rider Training"
                excerpt="Behind the scenes of our comprehensive safety and customer service training program."
                date="February 20, 2026"
                readTime="5 min read"
              />
              <BlogPostCard
                title="Expanding Across Kampala"
                excerpt="New neighborhoods, more riders, better coverage — here's what's coming next."
                date="February 15, 2026"
                readTime="4 min read"
              />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/20 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex items-center">
              <img src="/logo.png" alt="24Boda" className="h-8 w-auto" />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              © {new Date().getFullYear()} 24Boda. All rights reserved. Kampala, Uganda.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BlogPostCard({
  title,
  excerpt,
  date,
  readTime,
}: {
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
}) {
  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-lg">
      <div className="aspect-video bg-secondary"></div>
      <div className="p-6">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="size-3" />
            {date}
          </div>
          <div className="flex items-center gap-1">
            <Clock className="size-3" />
            {readTime}
          </div>
        </div>
        <h3 className="mt-3 text-lg font-semibold text-foreground">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{excerpt}</p>
        <button className="mt-4 text-sm font-medium text-primary transition-colors hover:text-primary/80">
          Read more →
        </button>
      </div>
    </Card>
  );
}
